import axios from 'axios';

// Leave REACT_APP_BACKEND_URL empty when the API is reverse-proxied under the
// same Hostinger domain. This also keeps local builds from requesting
// "undefined/api".
export const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('fitsocial_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => {
    window.dispatchEvent(new CustomEvent('fitcore:api-recovered'));
    return res;
  },
  (err) => {
    if (err.response?.status === 401 && !window.location.pathname.startsWith('/auth')) {
      localStorage.removeItem('fitsocial_token');
      localStorage.removeItem('fitsocial_user');
      window.location.href = '/auth';
    }
    if (!err.response || err.response.status >= 500) {
      window.dispatchEvent(new CustomEvent('fitcore:api-error'));
    }
    return Promise.reject(err);
  }
);

// SSE streaming helper for AI chat
export async function streamChat(message, { onToken, onDone, onError, onLimit }) {
  const token = localStorage.getItem('fitsocial_token');
  try {
    const res = await fetch(`${API}/ai/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ message }),
    });
    if (res.status === 429) { onLimit?.(); return; }
    if (!res.ok) { onError?.('AI service error'); return; }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split('\n\n');
      buffer = parts.pop();
      for (const part of parts) {
        const line = part.trim();
        if (!line.startsWith('data:')) continue;
        try {
          const data = JSON.parse(line.slice(5).trim());
          if (data.token) onToken?.(data.token);
          if (data.error) onError?.(data.error);
          if (data.done) onDone?.(data.remaining);
        } catch (e) { /* partial json */ }
      }
    }
  } catch (e) {
    onError?.('Connection error');
  }
}

export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Upload a supported image/video up to 100MB. Returns {id, url, content_type, size}.
export async function uploadMedia(file, onProgress) {
  const form = new FormData();
  form.append('file', file);
  const res = await api.post('/media/upload', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 0,
    onUploadProgress: (e) => {
      if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
    },
  });
  return res.data;
}

export async function deleteUploadedMedia(url) {
  const match = /^\/api\/media\/([0-9a-fA-F-]{36})$/.exec(url || '');
  if (!match) return;
  await api.delete(`/media/${match[1]}`);
}

// Resolve stored media URLs (/api/media/...) to absolute backend URLs
export function resolveMediaUrl(url) {
  if (!url) return url;
  if (url.startsWith('/api/') && BACKEND_URL) return `${BACKEND_URL}${url}`;
  return url;
}

// Read video duration (seconds) from a File before uploading
export function getVideoDuration(file) {
  return new Promise((resolve) => {
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.onloadedmetadata = () => {
      const d = v.duration;
      URL.revokeObjectURL(v.src);
      resolve(isFinite(d) ? d : 0);
    };
    v.onerror = () => { URL.revokeObjectURL(v.src); resolve(0); };
    v.src = URL.createObjectURL(file);
  });
}

export function timeAgo(iso) {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}
